import { Component, Input, OnInit, ViewChild, inject } from "@angular/core";
import * as FileSaver from "file-saver";
import {
  ConfirmationService,
  MessageService,
  SelectItem
} from "primeng/api";
import { Table, TableModule } from "primeng/table";
import {
  EntityModelSubmission,
  Study,
  SubmissionControllerService,
  User
} from "../../../../modules/lipidcompass-backend-client";
import { Pageable } from "../../../../modules/lipidcompass-backend-client/model/pageable";
import {
  ImportAdminControllerService,
  JobsService,
  JobSubmissionInputDto
} from "../../../../modules/lipidcompass-data-importer-client";
import {
  paging,
  submissionWorkflowConfig
} from "../../../environments/environment";
import { ColumnFilterType, TableColumn } from "../../_models/table-column";
import { LoggingService } from "../../_services/logging.service";
import { Router } from "@angular/router";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ButtonModule } from "primeng/button";
import { ButtonGroupModule } from "primeng/buttongroup";
import { TooltipModule } from "primeng/tooltip";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { EllipsisPipe } from "../../_pipes/ellipsis.pipe";
import { CommonModule, DatePipe, JsonPipe } from "@angular/common";

/**
 * The submission-management endpoint (`SubmissionControllerService#submissionGet`)
 * only accepts page/size/sort -- there is no server-side filter parameter. Per-column
 * filtering (#340 UI review Fix 3) is therefore done CLIENT-SIDE: the table loads up
 * to this many submissions in a single non-lazy page and lets PrimeNG filter/sort/
 * paginate locally. If the submission volume ever exceeds this cap, some submissions
 * would silently be un-filterable -- follow-up: add a server-side filter endpoint if/
 * when submission volume approaches this limit.
 */
const MAX_SUBMISSIONS_PAGE_SIZE = 500;

@Component({
  selector: "app-admin-dataimport-submission",
  templateUrl: "./admin-dataimport-submission.component.html",
  styleUrls: ["./admin-dataimport-submission.component.css"],
  standalone: true,
  imports: [
    ButtonModule,
    ButtonGroupModule,
    CommonModule,
    ConfirmDialogModule,
    DatePipe,
    EllipsisPipe,
    FormsModule,
    InputTextModule,
    JsonPipe,
    SelectModule,
    TableModule,
    TooltipModule
  ]
})
export class AdminDataimportSubmissionComponent implements OnInit {
  private submissionController = inject(SubmissionControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private jobControllerService = inject(JobsService);
  private importAdminController = inject(ImportAdminControllerService);
  private loggingService = inject(LoggingService);
  private router = inject(Router);

  @ViewChild("ads") dataTable: Table | undefined;

  // @Input() tableId: string;
  pagedItems: any[];

  columns: TableColumn[];
  filterFields: string[];

  @Input()
  title: string = "Study Submissions";

  loading: boolean = true;

  // No totalRecords binding on the table: it is not lazy, so PrimeNG derives the
  // count from the rows it holds -- and from the filtered rows while a column
  // filter is active, which an explicit total could not express. It also reads
  // that input only on its first change (Table.onChanges), so binding a field
  // that starts out undefined pinned the paginator at "0 of 0" and hid every row
  // past the first page.
  // spread operator to shallow clone; only used for the paginator's initial
  // (client-side) rows-per-page -- the server request always asks for the
  // full up-to-{@link MAX_SUBMISSIONS_PAGE_SIZE} page (see loadItems()).
  pageable: Pageable = { ...paging.defaultPageable };

  /** Dropdown options for the Status/Visibility per-column filters (#340 UI review Fix 3). */
  readonly statusOptions: SelectItem[] = [
    { label: "In Progress", value: EntityModelSubmission.StatusEnum.IN_PROGRESS },
    { label: "Submitted", value: EntityModelSubmission.StatusEnum.SUBMITTED },
    { label: "In Review", value: EntityModelSubmission.StatusEnum.IN_REVIEW },
    { label: "In Curation", value: EntityModelSubmission.StatusEnum.IN_CURATION },
    { label: "Published", value: EntityModelSubmission.StatusEnum.PUBLISHED }
  ];
  readonly visibilityOptions: SelectItem[] = [
    { label: "Private", value: EntityModelSubmission.VisibilityEnum.PRIVATE },
    { label: "Restricted", value: EntityModelSubmission.VisibilityEnum.RESTRICTED },
    { label: "Public", value: EntityModelSubmission.VisibilityEnum.PUBLIC }
  ];

  ngOnInit(): void {
    this.loadItems();
  }

  /**
   * Loads up to {@link MAX_SUBMISSIONS_PAGE_SIZE} submissions in a single
   * non-lazy page (the submission endpoint has no server-side filter param --
   * see the class-level doc comment) and flattens the nested study/submitter
   * fields onto each row so PrimeNG's `p-columnFilter` can filter on them
   * directly (#340 UI review Fix 3).
   */
  loadItems(sort: string[] = []): void {
    this.loading = true;
    this.loggingService.debug(
      "AdminDataimportSubmissionComponent",
      "Loading up to " +
        MAX_SUBMISSIONS_PAGE_SIZE +
        " submissions in a single page for client-side filtering/sorting/" +
        "pagination (no server-side filter endpoint exists -- see class doc); sort=" +
        JSON.stringify(sort)
    );
    this.submissionController
      .submissionGet(0, MAX_SUBMISSIONS_PAGE_SIZE, sort)
      .subscribe(pagedModel => {
        this.loggingService.debug(
          "AdminDataimportSubmissionComponent",
          "Received response: " + JSON.stringify(pagedModel)
        );
        const submissions = (pagedModel?._embedded?.submissions ??
          []) as EntityModelSubmission[];
        this.pagedItems = submissions.map(submission =>
          this.flattenSubmission(submission)
        );
        this.columns = [
          {
            field: "id",
            header: "Id",
            filterType: ColumnFilterType.Text,
            content: value => value,
            visible: true
          },
          {
            field: "studyNativeId",
            header: "Study ID",
            filterType: ColumnFilterType.Text,
            content: value => value,
            visible: true
          },
          {
            field: "studyTitleDescription",
            header: "Study Title / Description",
            filterType: ColumnFilterType.Text,
            content: value => value,
            visible: true
          },
          {
            field: "status",
            header: "Status",
            filterType: ColumnFilterType.Text,
            content: value => value,
            visible: true
          },
          {
            field: "visibility",
            header: "Visibility",
            filterType: ColumnFilterType.Text,
            content: value => value,
            visible: true
          },
          {
            field: "submitterName",
            header: "Submitter",
            filterType: ColumnFilterType.Text,
            content: value => value,
            visible: true
          },
          {
            field: "dateCreated",
            header: "Date Created",
            filterType: ColumnFilterType.Date,
            content: value => value,
            visible: true
          },
          {
            field: "dateLastModified",
            header: "Date Last Modified",
            filterType: ColumnFilterType.Date,
            content: value => value,
            visible: true
          }
        ];
        this.filterFields = this.columns.map(value => {
          return value.field;
        });
        // The paginator counts the rows above rather than the server's
        // total-across-all-pages, so it never advertises more than are locally
        // available. That falls out of the table deriving its own total.
        this.loading = false;
      });
  }

  /** Flattens the nested `study`/`submitter` fields onto the row so PrimeNG's
   * field-path column filters (Study ID, Study Title/Description, Submitter)
   * can reach them directly (#340 UI review Fix 3). */
  private flattenSubmission(submission: EntityModelSubmission): any {
    const study: Study | undefined = submission.study;
    const submitter: User | undefined = submission.submitter;
    return {
      ...submission,
      studyNativeId: study?.nativeId ?? "",
      studyTitleDescription: `${study?.name ?? ""} ${study?.description ?? ""}`.trim(),
      submitterName: `${submitter?.firstName ?? ""} ${submitter?.familyName ?? ""}`.trim()
    };
  }

  reload() {
    this.loadItems();
  }

  startImport(rowData: EntityModelSubmission, publishImmediately: boolean) {
    this.confirmationService.confirm({
      message:
        (publishImmediately ? "Import AND publish " : "Import for review ") +
        rowData.id +
        "?",
      accept: () => {
        const jobSubmission = <JobSubmissionInputDto>{
          maxRows: -1,
          submissionId: rowData.id,
          publishImmediately: publishImmediately
        };
        this.jobControllerService.queueSubmissionImportJob(jobSubmission).subscribe(
          () => {
            this.messageService.add({
              severity: "success",
              summary: "Successful",
              detail:
                "Sent " +
                rowData.id +
                (publishImmediately ? " to import & publish" : " to import for review"),
              life: 3000
            });
            this.reload();
          },
          error => {
            if (error.status == 409) {
              this.messageService.add({
                severity: "warn",
                summary: "Conflict",
                detail: "A submission job is already queued to import for " + rowData.id,
                life: 3000
              });
            } else {
              this.messageService.add({
                severity: "danger",
                summary: "Failed",
                detail: "Failed to send " + rowData.id + " to import",
                life: 3000
              });
            }
            this.reload();
          }
        );
      }
    });
  }

  publish(rowData: EntityModelSubmission) {
    this.confirmationService.confirm({
      message: "Publish " + rowData.id + "? This makes the study and its datasets public.",
      accept: () => {
        this.importAdminController.publish(rowData.id!).subscribe(
          () => {
            this.messageService.add({
              severity: "success",
              summary: "Successful",
              detail: "Published " + rowData.id,
              life: 3000
            });
            this.reload();
          },
          error => {
            this.messageService.add({
              severity: "danger",
              summary: "Failed",
              detail:
                error.status == 409
                  ? "Cannot publish " + rowData.id + " from its current status"
                  : "Failed to publish " + rowData.id,
              life: 3000
            });
            this.reload();
          }
        );
      }
    });
  }

  toggleCuration(rowData: EntityModelSubmission) {
    this.confirmationService.confirm({
      message:
        (rowData.status === "IN_CURATION" ? "Unmark " : "Mark ") +
        rowData.id +
        " for curation?",
      accept: () => {
        this.submissionController.submissionToggleCuration(rowData.id!).subscribe(
          () => {
            this.messageService.add({
              severity: "success",
              summary: "Successful",
              detail: "Toggled curation state for " + rowData.id,
              life: 3000
            });
            this.reload();
          },
          error => {
            this.messageService.add({
              severity: "danger",
              summary: "Failed",
              detail:
                error.status == 409
                  ? "Cannot toggle curation for " + rowData.id + " from its current status"
                  : "Failed to toggle curation for " + rowData.id,
              life: 3000
            });
            this.reload();
          }
        );
      }
    });
  }

  unimport(rowData: EntityModelSubmission) {
    this.confirmationService.confirm({
      message:
        "Unimport " +
        rowData.id +
        "? This deletes all imported data and resets the submission to SUBMITTED.",
      accept: () => {
        this.importAdminController.unimport(rowData.id!).subscribe(
          () => {
            this.messageService.add({
              severity: "success",
              summary: "Successful",
              detail: "Unimported " + rowData.id,
              life: 3000
            });
            this.reload();
          },
          error => {
            this.messageService.add({
              severity: "danger",
              summary: "Failed",
              detail:
                error.status == 409
                  ? "Cannot unimport " + rowData.id + " from its current status"
                  : "Failed to unimport " + rowData.id,
              life: 3000
            });
            this.reload();
          }
        );
      }
    });
  }

  edit(rowData: EntityModelSubmission) {
    this.loggingService.debug(
      "AdminDataimportSubmissionComponent",
      "Edit rowData: " + JSON.stringify(rowData)
    );
    // use router to switch to edit page
    // this.router.navigate(['/submit/study/', rowData.id]);
    var routerLocation = [
      "/",
      "submit",
      submissionWorkflowConfig.workflows.mzTab.steps[0].routerLink,
      rowData?.id
    ];
    this.loggingService.debug(
      "AdminDataimportSubmissionComponent",
      "Navigating to submission workflow at " +
        routerLocation +
        " for rowData: " +
        JSON.stringify(rowData)
    );
    this.router.navigate(routerLocation);
    // this.submissionController.saveSingle(rowData).subscribe(
    //     (response) => {
    //         this.messageService.add({
    //             severity: "success",
    //             summary: "Successful",
    //             detail: "Submission edited",
    //             life: 3000,
    //         });
    //         this.reload();
    //     },
    //     (error) => {
    //         this.messageService.add({
    //             severity: "danger",
    //             summary: "Failed",
    //             detail: "Failed to edit " + rowData.id,
    //             life: 3000,
    //         });
    //         this.reload();
    //     }
    // );
  }

  delete(rowData: EntityModelSubmission) {
    this.loggingService.debug(
      "AdminDataimportSubmissionComponent",
      "Delete rowData: " + JSON.stringify(rowData)
    );
    const submissionId = rowData?.id;
    if (!submissionId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Cannot delete submission with missing ID",
        life: 3000
      });
      return;
    }
    this.confirmationService.confirm({
      message: "Are you sure you want to delete " + submissionId + "?",
      // header: "Confirm",
      // icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.submissionController.submissionDeleteById(submissionId).subscribe(
          response => {
            this.messageService.add({
              severity: "success",
              summary: "Successful",
              detail: "Deleted " + submissionId,
              life: 3000
            });
            this.reload();
          },
          error => {
            this.messageService.add({
              severity: "danger",
              summary: "Failed",
              detail: "Failed to delete " + submissionId,
              life: 3000
            });
            this.reload();
          }
        );
      }
    });
  }

  exportExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.pagedItems);
      const workbook = {
        Sheets: { data: worksheet },
        SheetNames: ["data"]
      };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });
      this.saveAsExcelFile(excelBuffer, "lipidcompass-table");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  applyFilterGlobal($event: any, stringVal: string) {
    this.dataTable?.filterGlobal(
      ($event.target as HTMLInputElement).value,
      stringVal
    );
  }
}
